/**
 * List View Component
 * 
 * A hierarchical list visualization of the recipe graph.
 * This is a visualization mode that can work in both view and edit modes.
 */

import { DisplayNode } from '../recipe_renderer/types.js';
import { ListNode, ListViewState, ListViewConfig, DEFAULT_LIST_VIEW_CONFIG } from './types.js';
import { ListNodeItem } from './ListNodeItem.js';
import { ListDragController, ListDragResult } from './ListDragController.js';
import { EditionMode } from '../store/types.js';
import type { RecipeStore } from '../store/types.js';

export class ListView {
  private container: HTMLElement;
  private store: RecipeStore;
  private config: ListViewConfig;

  // Callbacks
  private onEditNode?: (nodeId: string) => void;
  private onDeleteNode?: (nodeId: string) => void;
  private onToggleExpand?: (nodeId: string) => void;
  private onToggleSelect?: (nodeId: string) => void;
  private onToggleExpandMainRoot?: (nodeId: string) => Promise<void>;
  private onDeselectRecipe?: () => void;
  private onCompletePickNode?: (nodeId: string) => void;
  private onReorderSiblings?: (parentId: string, newOrder: string[]) => void;
  private onReparentNode?: (nodeId: string, newParentId: string, insertIndex?: number) => void;
  private onOrphanNode?: (nodeId: string) => void;
  // Moved here from the shared EditToolbar (which used to show them
  // regardless of active tab) — go-to-ingredient/elaboration apply per
  // node, same as edit/delete, so each row now carries its own instead.
  private onGoToIngredient?: (nodeId: string) => void;
  private onElaborationClick?: (nodeId: string) => void;
  private onRemoveElaboration?: (nodeId: string) => void;
  // Same story — [↧]/[↥]/[↟] moved here from the shared EditToolbar too.
  private onAddChild?: (nodeId: string) => void;
  private onAddParent?: (nodeId: string) => void;
  private onTransform?: (nodeId: string) => void;
  // Fired when the load-more sentinel (see render()'s footer) scrolls
  // into view and no fetch is already in flight — HomePage owns the
  // actual pagination (which recipes, what offset); this view only
  // reports "the user scrolled near the bottom" and reflects whatever
  // status HomePage reports back via setLoadMoreStatus(). This first
  // appeared not to work at all — turned out to be a separate layout
  // bug (home-list-wide was missing its own 'flex flex-col', so
  // this.container's 'flex-1 min-h-0' never took effect and it just
  // grew to fit all its content instead of clipping/scrolling — see
  // HomePage.ts's own comment on listAreaWide), now fixed.
  private onLoadMore?: () => void;

  /** Whether another page of recipes is being fetched, or none remain —
   *  drives the footer's text and whether the sentinel still triggers
   *  onLoadMore(). Set from outside via setLoadMoreStatus(); render()
   *  only reads it, never changes it, so a load that's still in flight
   *  across a re-render (e.g. a store notify from an unrelated change)
   *  doesn't get silently reset to 'idle' and double-fire. */
  private loadMoreStatus: 'idle' | 'loading' | 'exhausted' = 'idle';
  /** Root count as of the last render() — footerText() needs this when
   *  setLoadMoreStatus() updates the footer directly, without a full
   *  re-render. */
  private lastRootCount = 0;
  private loadMoreFooterEl: HTMLElement | null = null;
  private loadMoreObserver: IntersectionObserver | null = null;

  // Local, view-only state: node whose delete button is armed, awaiting a
  // second confirming click. Not store state — ephemeral to this view,
  // mirrors GraphVisualizer's EditButtonRenderer arm/confirm pattern.
  private armedDeleteNodeId: string | null = null;

  // Local, session-only sibling order overrides for *normal*-mode drags —
  // rearranging is purely visual there (not persisted), same idea as
  // Graph View's node positions. Keyed by parent node id. Cleared on
  // entering edit mode (where the real, persisted order takes over) and
  // whenever a recipe (re)loads.
  private localChildOrderOverrides: Map<string, string[]> = new Map();
  private wasEditMode: boolean;

  private dragController: ListDragController;

  // A completed drag can leave a trailing native `click` event on
  // whatever the pointer released over — suppress exactly the next one so
  // it's never misread as a background click and deselects right after
  // a reorder/reparent. Cleared on a short timeout too, in case no click
  // ever actually follows (browsers block click synthesis after a touch
  // drag that called preventDefault).
  private suppressNextBackgroundClick = false;

  private _lastIsWideScreen: boolean;
  private _resizeHandler: (() => void) | null = null;

  constructor(
    container: HTMLElement,
    store: RecipeStore,
    config: Partial<ListViewConfig> = {}
  ) {
    this.container = container;
    this.store = store;
    this.config = { ...DEFAULT_LIST_VIEW_CONFIG, ...config };
    this._lastIsWideScreen = this.isWideScreen();
    this.wasEditMode = this.store.isEditMode();

    this.dragController = new ListDragController(this.container, {
      isEditMode: () => this.store.isEditMode(),
      getRecipeId: (nodeId: string) => this.store.getNode(nodeId)?.recipeId,
      wouldCreateCycle: (nodeId: string, candidateParentId: string) =>
        nodeId === candidateParentId || this.store.isDescendantOf(candidateParentId, nodeId),
      onDragStateChange: (dragging: boolean) => {
        if (!dragging) {
          this.suppressNextBackgroundClick = true;
          setTimeout(() => { this.suppressNextBackgroundClick = false; }, 0);
        }
        // Whether it started (suppress re-render mid-drag, see render())
        // or ended (catch up on anything missed while suppressed, and
        // reflect the drop's outcome), a render is exactly what's needed.
        this.render();
      },
      onDrop: (result: ListDragResult) => this.handleDragDrop(result),
    });

    // Clicking empty space (not a row) deselects — same effect as Graph
    // View's own background click.
    this.container.addEventListener('click', (e: MouseEvent) => {
      if (this.suppressNextBackgroundClick) {
        this.suppressNextBackgroundClick = false;
        return;
      }
      if ((e.target as HTMLElement).closest('.list-node-item')) return;
      this.deselectSelection();
    });

    this.store.on('recipeLoaded', () => {
      this.localChildOrderOverrides.clear();
    });

    // Subscribe to store changes for automatic re-rendering
    this.store.subscribe(() => {
      if (!this.store.isEditMode() && this.armedDeleteNodeId !== null) {
        this.armedDeleteNodeId = null;
      }
      const isEditMode = this.store.isEditMode();
      if (isEditMode && !this.wasEditMode) {
        // Entering edit mode: the real, persisted order is the source of
        // truth from here on — drop any session-only visual rearrangement.
        this.localChildOrderOverrides.clear();
      }
      this.wasEditMode = isEditMode;

      if (!this.dragController.isActive()) {
        this.render();
      }
    });

    // Crossing the wide-screen breakpoint changes whether items show the
    // ✎ edit button (hidden on wide — the side panel already shows the
    // same editable tabs inline there) — same breakpoint GraphVisualizer
    // uses for the equivalent side-panel/toolbar behavior.
    this._resizeHandler = () => {
      const nowWide = this.isWideScreen();
      if (nowWide !== this._lastIsWideScreen) {
        this._lastIsWideScreen = nowWide;
        this.render();
      }
    };
    window.addEventListener('resize', this._resizeHandler);

    this.render();
  }

  /**
   * Whether the viewport is at the wide-screen layout breakpoint — same
   * `lg:` (1024px) cutoff the rest of the responsive layout uses.
   */
  private isWideScreen(): boolean {
    return window.innerWidth >= 1024;
  }

  /**
   * Set edit mode (shows/hides edit buttons)
   */
  setEditMode(editMode: boolean): void {
    this.store.setEditMode(editMode);
    this.config.showEditButtons = editMode;
    this.armedDeleteNodeId = null;
    this.render();
  }

  /**
   * Set callbacks for user interactions
   */
  setCallbacks(callbacks: {
    onEditNode?: (nodeId: string) => void;
    onDeleteNode?: (nodeId: string) => void;
    onToggleExpand?: (nodeId: string) => void;
    onToggleSelect?: (nodeId: string) => void;
    onToggleExpandMainRoot?: (nodeId: string) => Promise<void>;
    onDeselectRecipe?: () => void;
    onCompletePickNode?: (nodeId: string) => void;
    onReorderSiblings?: (parentId: string, newOrder: string[]) => void;
    onReparentNode?: (nodeId: string, newParentId: string, insertIndex?: number) => void;
    onOrphanNode?: (nodeId: string) => void;
    onGoToIngredient?: (nodeId: string) => void;
    onElaborationClick?: (nodeId: string) => void;
    onRemoveElaboration?: (nodeId: string) => void;
    onAddChild?: (nodeId: string) => void;
    onAddParent?: (nodeId: string) => void;
    onTransform?: (nodeId: string) => void;
    onLoadMore?: () => void;
  }): void {
    this.onEditNode = callbacks.onEditNode;
    this.onDeleteNode = callbacks.onDeleteNode;
    this.onToggleExpand = callbacks.onToggleExpand;
    this.onToggleSelect = callbacks.onToggleSelect;
    this.onToggleExpandMainRoot = callbacks.onToggleExpandMainRoot;
    this.onDeselectRecipe = callbacks.onDeselectRecipe;
    this.onCompletePickNode = callbacks.onCompletePickNode;
    this.onReorderSiblings = callbacks.onReorderSiblings;
    this.onReparentNode = callbacks.onReparentNode;
    this.onOrphanNode = callbacks.onOrphanNode;
    this.onGoToIngredient = callbacks.onGoToIngredient;
    this.onElaborationClick = callbacks.onElaborationClick;
    this.onRemoveElaboration = callbacks.onRemoveElaboration;
    this.onAddChild = callbacks.onAddChild;
    this.onAddParent = callbacks.onAddParent;
    this.onTransform = callbacks.onTransform;
    this.onLoadMore = callbacks.onLoadMore;
  }

  /**
   * Called by HomePage to reflect the actual state of a load-more fetch
   * it's driving — this view has no network access of its own. Updates
   * the footer directly (not a full render()) so a status flip never
   * disturbs scroll position or rebuilds the (potentially large) tree
   * just to change one line of text. 'exhausted' also disconnects the
   * sentinel observer — there's nothing left to page in for the current
   * filter, so it would otherwise just sit there never firing again
   * anyway, but disconnecting makes that explicit rather than implicit.
   */
  setLoadMoreStatus(status: 'idle' | 'loading' | 'exhausted'): void {
    this.loadMoreStatus = status;
    if (this.loadMoreFooterEl) {
      this.loadMoreFooterEl.textContent = this.footerText();
    }
    if (status === 'exhausted') {
      this.loadMoreObserver?.disconnect();
    }
  }

  private footerText(): string {
    const base = this.lastRootCount === 1 ? '1 recepta carregada' : `${this.lastRootCount} receptes carregades`;
    return this.loadMoreStatus === 'loading' ? `${base} — carregant més…` : base;
  }

  /**
   * Set the currently selected recipe
   */
  setSelectedRecipe(recipeId: string | null): void {
    this.store.selectRecipe(recipeId);
    this.render();
  }

  /**
   * Render the list view
   */
  render(): void {
    // this.container is itself the scrollable element (see where it's
    // constructed in HomePage.ts) — clearing/rebuilding its content resets
    // scrollTop to 0 in most browsers (there's a moment with nothing to
    // scroll), even though the container node itself never changes. Every
    // store change re-renders (selecting a node included), so without
    // this, selecting anything below the fold would always snap the list
    // back to the top.
    const scrollTop = this.container.scrollTop;

    // Clear container
    this.container.innerHTML = '';

    // Find root nodes (nodes with no parents)
    const roots = this.findRootNodes();

    // Filter out hidden roots — the same source of truth Graph View uses
    // (covers focus, search filtering, and explicitly hidden recipes alike,
    // and can't drift out of sync the way re-deriving from a separate
    // "focused recipe" flag could).
    const visibleRoots = roots.filter(node => !this.store.isNodeHidden(node.id));

    // Build tree structure
    const tree = this.buildTree(visibleRoots, 0);
    
    // Render tree
    const listElement = document.createElement('div');
    listElement.className = 'list-view';
    listElement.style.cssText = `
      font-family: 'Courier New', Consolas, 'Liberation Mono', monospace;
    `;
    
    tree.forEach(node => {
      this.renderListNode(node, listElement);
    });

    // Footer: how many recipes are loaded, doubling as the infinite-
    // scroll trigger (a sentinel this view watches via
    // IntersectionObserver). Only meaningful in the plain browse list —
    // once a recipe is focused, this view shows just that one recipe's
    // own tree (every other root hidden below), so "load more browse
    // results" has nothing to do with what's on screen; paging in more
    // of the browse set behind it would be pointless (and, worse, kept
    // scrolling the still-attached-but-invisible sentinel further out
    // of reach, pulling in the ENTIRE remaining browse set in the
    // background for no visible reason — see onFocusChanged's restore
    // path, which re-fetches it all again on unfocus anyway). Rebuilt
    // every render() since this.container.innerHTML = '' above already
    // destroyed the previous one along with everything else.
    this.loadMoreFooterEl = null;
    this.loadMoreObserver?.disconnect();
    this.loadMoreObserver = null;
    if (this.store.getFocusedRecipeId() === null) {
      this.lastRootCount = roots.length;
      const footer = document.createElement('div');
      footer.className = 'list-view-footer text-center text-xs text-[#888] font-mono py-3';
      footer.textContent = this.footerText();
      listElement.appendChild(footer);
      this.loadMoreFooterEl = footer;

      if (this.loadMoreStatus !== 'exhausted') {
        this.loadMoreObserver = new IntersectionObserver((entries) => {
          if (entries[0]?.isIntersecting && this.loadMoreStatus === 'idle') {
            this.onLoadMore?.();
          }
        }, { root: this.container, rootMargin: '200px' });
        this.loadMoreObserver.observe(footer);
      }
    }

    this.container.appendChild(listElement);
    this.container.scrollTop = scrollTop;
  }

  /**
   * Find root nodes (nodes with no parents in the pool)
   */
  private findRootNodes(): DisplayNode[] {
    const allNodes = Array.from(this.store.getState().nodePool.values());
    const childIds = new Set<string>();
    
    // Collect all child IDs
    allNodes.forEach(node => {
      node.data.children.forEach(childId => childIds.add(childId));
    });
    
    // Return nodes that are not children of any other node
    return allNodes.filter(node => !childIds.has(node.id));
  }

  /**
   * Build tree structure from root nodes
   */
  private buildTree(roots: DisplayNode[], level: number): ListNode[] {
    return roots.map(node => {
      const isExpanded = this.store.isNodeExpanded(node.id);
      const listNode: ListNode = {
        displayNode: node,
        level,
        isExpanded: isExpanded,
        isSelected: this.store.isNodeSelected(node.id),
        children: [],
      };

      // If expanded, build children. A child can have more than one parent
      // (e.g. a shared ingredient used in two steps) — it's the same node
      // wherever it's rendered (same id, so selecting/folding it anywhere
      // selects/folds it everywhere), and it's drawn under each expanded
      // parent that has it, same as Graph View draws one node with two
      // incoming links. What we must still respect is per-node visibility:
      // a child hidden by a *different* parent folding (hideNode hides it
      // unconditionally for every parent, not just the one that folded)
      // must not be shown here just because this parent stayed expanded.
      if (listNode.isExpanded && node.data.children.length > 0) {
        const orderedChildIds = this.effectiveChildOrder(node);
        const childNodes = orderedChildIds
          .filter(childId => !this.store.isNodeHidden(childId))
          .map(childId => this.store.getNode(childId))
          .filter((child): child is DisplayNode => child !== undefined);

        listNode.children = this.buildTree(childNodes, level + 1);
      }

      return listNode;
    });
  }

  /**
   * A node's children in rendering order — the real stored order, unless
   * a normal-mode drag left a session-only override for it. Any child no
   * longer actually there is dropped, and any real child missing from a
   * stale override is appended, so the override never hides genuine data.
   */
  private effectiveChildOrder(node: DisplayNode): string[] {
    const override = this.localChildOrderOverrides.get(node.id);
    if (!override) return node.data.children;

    const currentSet = new Set(node.data.children);
    const ordered = override.filter(id => currentSet.has(id));
    for (const id of node.data.children) {
      if (!ordered.includes(id)) ordered.push(id);
    }
    return ordered;
  }

  /**
   * Render a list node and its children
   */
  private renderListNode(node: ListNode, parentElement: HTMLElement): void {
    const item = new ListNodeItem(
      node,
      this.config,
      this.store.isEditMode(),
      this.armedDeleteNodeId === node.displayNode.id,
      // Hidden on wide screens (inline side-panel editing there) and
      // whenever 2+ nodes are multi-selected (editing one node at a time
      // doesn't apply — use the toolbar's multi-select actions instead).
      !this.isWideScreen() && this.store.getSelectedNodes().length <= 1,
      // "Expanding" a selected row (full title, image, other details) is a
      // single-select affordance — in multi-select mode, isSelected marks
      // membership in a multi-node picking operation, not "show me this
      // one's details", so rows stay collapsed regardless.
      this.store.isMultiSelectMode(),
      this.store,
      {
        onToggleExpand: () => this.handleToggleExpand(node.displayNode.id),
        onToggleSelect: () => this.handleToggleSelect(node.displayNode.id),
        onEditClick: () => {
          this.armedDeleteNodeId = null;
          this.onEditNode?.(node.displayNode.id);
        },
        onDeleteClick: () => this.handleDeleteClick(node.displayNode.id),
        onGoToIngredient: () => this.onGoToIngredient?.(node.displayNode.id),
        onElaborationClick: () => this.onElaborationClick?.(node.displayNode.id),
        onRemoveElaboration: () => this.onRemoveElaboration?.(node.displayNode.id),
        onAddChild: () => this.onAddChild?.(node.displayNode.id),
        onAddParent: () => this.onAddParent?.(node.displayNode.id),
        onTransform: () => this.onTransform?.(node.displayNode.id),
      }
    );

    const itemEl = item.render();
    itemEl.dataset.nodeId = node.displayNode.id;
    parentElement.appendChild(itemEl);

    // Dragging is independent of edit mode. Everything except the recipe's
    // actual main root is draggable — that includes top-level entries
    // that aren't the main root (a node orphaned by a previous drag, or a
    // freshly created node with no parent yet), so a brand-new node can
    // be dragged onto something to attach it in the first place.
    if (!node.displayNode.isMainRoot) {
      this.dragController.registerRow(itemEl, node.displayNode.id);
    }

    // Render children if expanded. No margin/indentation on this wrapper
    // itself — a block box with margin-left and an auto width shrinks to
    // still fit its parent, which would narrow every row's own box by
    // that much at every depth, compounding with nesting, so a deeply
    // nested row's background/border could never reach the list's true
    // right edge. Indentation instead lives on each row's own content
    // (see ListNodeItem, keyed off node.level) — this wrapper is purely
    // structural (grouping + drag-target bookkeeping), same width as its
    // parent.
    if (node.isExpanded && node.children.length > 0) {
      const childrenContainer = document.createElement('div');
      childrenContainer.className = 'list-children';
      childrenContainer.dataset.parentId = node.displayNode.id;

      node.children.forEach(child => {
        this.renderListNode(child, childrenContainer);
      });

      parentElement.appendChild(childrenContainer);
    }
  }

  /**
   * Interpret a resolved drag-and-drop result: persist it via
   * GraphVisualizer in edit mode, or apply a local-only visual reorder in
   * normal mode.
   */
  private handleDragDrop(result: ListDragResult): void {
    if (result.kind === 'orphan') {
      this.onOrphanNode?.(result.nodeId);
      return;
    }
    if (result.kind === 'reparent') {
      this.onReparentNode?.(result.nodeId, result.newParentId, result.insertIndex);
      return;
    }
    // reorder
    if (this.store.isEditMode()) {
      this.onReorderSiblings?.(result.parentId, result.newOrder);
    } else {
      this.localChildOrderOverrides.set(result.parentId, result.newOrder);
    }
  }

  /**
   * Handle a click on a node's delete (×) button.
   * First click arms it (disarming any other armed node); a second click
   * on the same armed node confirms the deletion.
   */
  private handleDeleteClick(nodeId: string): void {
    if (this.armedDeleteNodeId === nodeId) {
      this.armedDeleteNodeId = null;
      this.onDeleteNode?.(nodeId);
    } else {
      this.armedDeleteNodeId = nodeId;
      this.render();
    }
  }

  /**
   * Disarm any pending delete confirmation. Safe to call even when nothing
   * is armed. Called whenever the user does something else in the list —
   * selects, expands/collapses, or edit mode ends.
   */
  disarmDelete(): void {
    if (this.armedDeleteNodeId !== null) {
      this.armedDeleteNodeId = null;
      this.render();
    }
  }

  /**
   * Handle toggle expand.
   *
   * A main root's expand/collapse also focuses/unfocuses its recipe
   * (and, for unfocus, may need to confirm unsaved changes first) — that
   * guard lives in GraphVisualizer.toggleExpandNode(), the same entry
   * point Graph View's own caret and the header's focus button use, so
   * it's routed there via onToggleExpandMainRoot rather than duplicated
   * here. Non-root nodes have no focus implication, so they go straight
   * to the store as before.
   */
  private async handleToggleExpand(nodeId: string): Promise<void> {
    const node = this.store.getNode(nodeId);
    if (!node) return;

    this.armedDeleteNodeId = null;

    if (node.isMainRoot) {
      await this.onToggleExpandMainRoot?.(nodeId);
    } else if (this.store.isNodeExpanded(nodeId)) {
      this.store.collapseNode(nodeId);
    } else {
      await this.store.expandNode(nodeId);
    }

    this.onToggleExpand?.(nodeId);
    this.render();
  }

  /**
   * Handle toggle select.
   *
   * While picking a node for a transformation parameter (the node-edit
   * popup's "◎" button — see GraphVisualizer.completePickNode()'s doc
   * comment), a row click completes the pick instead of a normal select,
   * same as clicking a node on the graph canvas does.
   */
  private handleToggleSelect(nodeId: string): void {
    if (this.store.getEditionSubMode() === EditionMode.PICK_NODE) {
      this.onCompletePickNode?.(nodeId);
      return;
    }

    this.armedDeleteNodeId = null;
    if (this.store.isMultiSelectMode()) {
      this.store.toggleMultiSelectNode(nodeId);
    } else if (this.store.isNodeSelected(nodeId)) {
      // Clicking the already-selected row deselects it — selecting again
      // never toggles off on its own (selectNodeWithAutoRecipe always
      // (re)selects), so without this, the only way to deselect was
      // clicking a background area, which the list rarely has much of.
      this.deselectSelection();
      this.onToggleSelect?.(nodeId);
      return;
    } else {
      this.store.selectNodeWithAutoRecipe(nodeId);
    }

    this.onToggleSelect?.(nodeId);
    this.render();
  }

  /**
   * Deselect the current node — and, if the selected recipe isn't
   * focused (browsing unfocused, just happens to still be shown in the
   * header), the recipe too, same as Graph View's own background click.
   * Shared by the container's background click, the document-level
   * outside-click, and clicking the already-selected row itself.
   */
  private deselectSelection(): void {
    this.armedDeleteNodeId = null;
    this.store.deselect();
    if (this.store.getSelectedRecipeId() && !this.store.getFocusedRecipeId()) {
      this.onDeselectRecipe?.();
    }
  }

  /**
   * Get selected node IDs
   */
  getSelectedNodeIds(): string[] {
    const state = this.store.getState();
    return Array.from(state.selectedNodeIds);
  }

  /**
   * Clear selection
   */
  clearSelection(): void {
    this.store.deselectAllNodes();
    this.render();
  }

  /**
   * Expand all nodes
   */
  expandAll(): void {
    this.store.expandAllNodes();
    this.render();
  }

  /**
   * Collapse all nodes
   */
  collapseAll(): void {
    // Collapse all nodes by adding each to the collapsed set
    const allNodeIds = Array.from(this.store.getState().nodePool.keys());
    for (const nodeId of allNodeIds) {
      this.store.collapseNode(nodeId);
    }
    this.render();
  }

  /**
   * Destroy the list view
   */
  destroy(): void {
    this.dragController.cancel();
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }
    this.loadMoreObserver?.disconnect();
    this.loadMoreObserver = null;
    this.container.innerHTML = '';
  }
}
